/**
 * Plateforme multi événement e.labriquerie.net, (c) Mikaël FOURNY.
 * src/i18n/fr.envoisCourriels.ts
 *
 * Messages de la section « Envois de courriels » (/admin/email-composer,
 * 2026-10-08) : point de départ, filtres combinés, effectif, aperçu des
 * destinataires, essai à soi-même, récapitulatif avant envoi, historique.
 * Fusionné dans fr.ts.
 *
 * Plateforme multi-événements : ni « salon », ni « atelier », ni
 * « exposant », ni « visiteur » écrits en dur ; le vocabulaire passe par les
 * jetons {lex...}. Apostrophe typographique partout. Aucun tiret cadratin.
 *
 * Mikael - chef de projet - 2026-10-08
 */
export const messagesEnvoisCourrielsFr: Record<string, string> = {
  // ── Section ────────────────────────────────────────────────────────────
  'envois.lead':
    'Choisissez qui reçoit le courriel, rédigez-le, envoyez-vous un essai, puis confirmez. Les courriels partent par lots, quelques minutes après la confirmation.',
  'envois.etape.destinataires': '1. Destinataires',
  'envois.etape.message': '2. Message',
  'envois.etape.envoi': '3. Essai et envoi',

  // ── Point de départ (audiences historiques) ────────────────────────────
  'envois.base.legend': 'Point de départ',
  'envois.base.all': 'Tout le monde',
  'envois.base.visitors': '{lexVisitorPluralCap}',
  'envois.base.exhibitors': '{lexExhibitorPluralCap}',
  'envois.base.staff': 'Équipe',
  'envois.base.sans_photo': 'Sans photo de badge',
  'envois.base.filtres': 'Sélection par filtres',
  'envois.base.role': 'Rôle',

  // ── Filtres ────────────────────────────────────────────────────────────
  'envois.filtres.titre': 'Filtres',
  'envois.filtres.aide':
    'Les familles se cumulent (ET). Dans une même famille, cocher plusieurs cases élargit (OU). Une famille sans case cochée ne filtre rien.',
  'envois.filtres.actifs': '{n, plural, =0 {aucun filtre} one {# filtre actif} other {# filtres actifs}}',
  'envois.filtres.reinitialiser': 'Réinitialiser les filtres',

  'envois.profils.legend': 'Profil',
  'envois.profil.visiteur': '{lexVisitorCap}',
  'envois.profil.etudiant': 'Étudiant',
  'envois.profil.public_specifique': 'Public spécifique',
  'envois.profil.exposant': '{lexExhibitorCap} (compte principal)',
  'envois.profil.recruteur': 'Recruteur rattaché',
  'envois.profil.animateur': 'Animateur {lexWorkshopOf}',
  'envois.profil.equipe': 'Équipe de l’organisation',
  'envois.profil.administration': 'Comptes du back-office',
  'envois.profil.vip': 'Invité VIP',

  'envois.photo.legend': 'Photo de badge',
  'envois.photo.indifferent': 'Indifférent',
  'envois.photo.avec': 'Avec photo',
  'envois.photo.sans': 'Sans photo',

  'envois.comptes.legend': 'État du compte',
  'envois.comptes.actif': 'Actif',
  'envois.comptes.en_attente': 'En attente d’activation',
  'envois.comptes.aide': 'Par défaut, seuls les comptes actifs sont visés. Les comptes bloqués ou archivés ne le sont jamais.',

  'envois.fiche.legend': 'Fiche {lexExhibitorOf} incomplète',
  'envois.fiche.jobs': 'Aucune offre publiée',
  'envois.fiche.logo': 'Sans logo',
  'envois.fiche.description': 'Sans présentation',
  'envois.fiche.sector': 'Sans secteur',
  'envois.fiche.aide': 'Ne vise que les comptes principaux. Un organisme qui ne recrute pas n’est jamais visé pour les offres.',

  'envois.ateliers.legend': '{lexWorkshopPluralCap}',
  'envois.ateliers.nonPublies': 'Animateurs ayant {lexWorkshopIndef} en brouillon (non publié)',
  'envois.ateliers.inscrits': 'Inscrits (confirmés ou en liste d’attente) à :',
  'envois.ateliers.aucun': '{lexWorkshopNoneCap} pour le moment.',
  'envois.ateliers.ligne': '{titre} ({inscrits, plural, =0 {aucun inscrit} one {# inscrit} other {# inscrits}}){brouillon, select, oui {, en brouillon} other {}}',

  'envois.structures.legend': 'Structure',
  'envois.structures.recherche': 'Rechercher une structure',
  'envois.structures.aide': 'Le compte principal et ses recruteurs actifs.',
  'envois.structures.aucune': 'Aucune structure ne correspond.',

  'envois.dates.legend': 'Date d’inscription',
  'envois.dates.apres': 'Inscrits à partir du',
  'envois.dates.avant': 'Inscrits avant le',

  'envois.techniques.label': 'Inclure les comptes techniques (tablettes « Tablette N »)',

  // ── Effectif et aperçu ─────────────────────────────────────────────────
  'envois.effectif':
    '{n, plural, =0 {Personne ne recevra ce courriel} one {# personne recevra ce courriel} other {# personnes recevront ce courriel}}',
  'envois.effectif.calcul': 'Calcul de l’effectif…',
  'envois.effectif.erreur': 'Effectif indisponible. Vérifiez les filtres ou réessayez.',
  'envois.apercu.voir': 'Voir les destinataires',
  'envois.apercu.reste': '{n, plural, one {et # autre personne} other {et # autres personnes}}',
  'envois.apercu.masque': 'Coordonnées masquées : votre compte n’a pas le droit de les voir.',

  // ── Message ────────────────────────────────────────────────────────────
  'envois.sujet': 'Objet du courriel',
  'envois.corps': 'Message',

  // ── Essai ──────────────────────────────────────────────────────────────
  'envois.essai.moi': 'M’envoyer un essai',
  'envois.essai.autre': 'Ou envoyer l’essai à cette adresse',
  'envois.essai.autreBtn': 'Envoyer l’essai',
  'envois.essai.ok': 'Essai envoyé à {email}.',

  // ── Envoi ──────────────────────────────────────────────────────────────
  'envois.envoyer': 'Envoyer à {n, plural, one {# personne} other {# personnes}}',
  'envois.recap.titre': 'Confirmer l’envoi',
  'envois.recap.sujet': 'Objet',
  'envois.recap.criteres': 'Critères',
  'envois.recap.base': 'Point de départ : {base}',
  'envois.recap.lots': 'Les courriels partent par lots dans les minutes qui suivent. Vous pourrez annuler tant que la file n’est pas entièrement traitée.',
  'envois.recap.annuler': 'Revenir',
  'envois.recap.confirmer': 'Confirmer l’envoi',
  'envois.toast.file': 'Envoi confirmé : {n, plural, one {# courriel} other {# courriels}} en file.',
  'envois.err.effectifChange':
    'L’effectif a changé entre-temps ({n, plural, one {# personne} other {# personnes}}). Vérifiez puis confirmez à nouveau.',
  'envois.err.aucun': 'Aucun destinataire pour cette sélection.',
  'envois.err.filtres': 'Les filtres choisis sont invalides.',

  // ── Historique ─────────────────────────────────────────────────────────
  'envois.historique.titre': 'Historique des envois',
  'envois.historique.envoyes': '{n, plural, one {# envoyé} other {# envoyés}}',
  'envois.historique.echecs': '{n, plural, one {# échec} other {# échecs}}',
  'envois.historique.total': 'sur {n}',
  'envois.historique.annuler': 'Annuler l’envoi',
  'envois.historique.annulerConfirm': 'Annuler cet envoi ? Les courriels pas encore partis ne partiront pas.',
  'envois.historique.annule': 'Envoi annulé.',
  'envois.statut.draft': 'Brouillon',
  'envois.statut.queued': 'En file',
  'envois.statut.sending': 'En cours',
  'envois.statut.sent': 'Terminé',
  'envois.statut.failed': 'Échec',
  'envois.statut.cancelled': 'Annulé',
};
